import type {
  AddressFormValue,
  ReturnAddressFormValue,
} from '../../ship-from-ship-to/models/address-form.model';

/** Display model for a Ship From / Ship To review card. */
export interface ReviewAddressView {
  name: string;
  streetLines: string[];
  /** Suite/Apt./Unit shown as a labeled line (not a street line). */
  suiteAptUnit: string;
  /** Entry/Buzzer Code shown as a labeled line. */
  entryBuzzerCode: string;
  localityLine: string;
  /** Contact / attention name shown as a labeled line when present. */
  contactName: string;
  email: string;
  phone: string;
}

/** Display model for the Return To Address review card. */
export interface ReviewReturnAddressView {
  sameAsShipFrom: boolean;
  address: ReviewAddressView | null;
}

/** Inputs used to map the Return To Address card. */
export interface MapReviewReturnAddressInput {
  useDifferentReturnAddress: boolean;
  returnAddress: ReturnAddressFormValue;
  shipFrom: AddressFormValue;
}

/**
 * Formats a 10-digit North American phone number as XXX-XXX-XXXX.
 * Appends ` x{extension}` when an extension is present.
 */
export function formatPhoneNumber(phoneNumber: string, phoneExtension?: string): string {
  const formatted = /^\d{10}$/.test(phoneNumber)
    ? `${phoneNumber.slice(0, 3)}-${phoneNumber.slice(3, 6)}-${phoneNumber.slice(6)}`
    : phoneNumber;
  const extension = phoneExtension?.trim() ?? '';
  if (!extension) {
    return formatted;
  }

  return `${formatted} x${extension}`;
}

/**
 * Builds street lines from address fields, omitting blanks and suite (suite is labeled separately).
 */
function streetLinesFrom(address: {
  streetAddress: string;
  addressLine2: string;
  addressLine3: string;
}): string[] {
  return [address.streetAddress, address.addressLine2, address.addressLine3]
    .map((line) => line.trim())
    .filter((line) => line.length > 0);
}

/**
 * Removes repeated street lines while preserving the first trimmed representation.
 *
 * @param lines Street lines from an address form or shipment response
 */
export function deduplicateStreetLines(lines: string[]): string[] {
  const seen = new Set<string>();

  return lines
    .map((line) => line.trim())
    .filter((line) => {
      if (!line) {
        return false;
      }

      const comparable = line.toUpperCase().replace(/[^A-Z0-9]/g, '');
      if (!comparable) {
        return true;
      }
      if (seen.has(comparable)) {
        return false;
      }

      seen.add(comparable);
      return true;
    });
}

/**
 * Builds the city / province / postal / country locality line shown on review cards.
 */
function localityLineFrom(address: {
  city: string;
  province: string | null;
  postalCode: string;
}): string {
  const province = address.province?.trim() ?? '';
  const cityProvince = [address.city.trim(), province].filter((part) => part.length > 0).join(', ');
  const postal = address.postalCode.trim();
  const left = [cityProvince, postal].filter((part) => part.length > 0).join(' ');
  if (!left) {
    return 'Canada';
  }

  return `${left} Canada`;
}

/** Labels used when formatting suite / entry-code lines in the address block. */
export interface ReviewAddressBlockLabels {
  suiteAptLabel: string;
  buzzerLabel: string;
}

/** Empty suite/entry labels when callers omit the second argument. */
const EMPTY_ADDRESS_BLOCK_LABELS: ReviewAddressBlockLabels = {
  suiteAptLabel: '',
  buzzerLabel: '',
};

/**
 * Builds the compact address block lines (name → street → suite → entry code → locality).
 * Entry/buzzer sits directly under suite to match the ESN label layout (OM-763).
 * Callers supply translated suite/entry labels; empty fields are omitted.
 */
export function formatReviewAddressBlock(
  view: ReviewAddressView,
  labels?: ReviewAddressBlockLabels,
): string[] {
  const resolvedLabels = labels ?? EMPTY_ADDRESS_BLOCK_LABELS;
  const lines = [...(view.name.trim() ? [view.name.trim()] : []), ...view.streetLines];
  const suite = view.suiteAptUnit.trim();
  if (suite) {
    const prefix = resolvedLabels.suiteAptLabel.trim();
    lines.push(prefix ? `${prefix} ${suite}` : suite);
  }
  const buzzer = view.entryBuzzerCode.trim();
  if (buzzer) {
    const prefix = resolvedLabels.buzzerLabel.trim();
    lines.push(prefix ? `${prefix} ${buzzer}` : buzzer);
  }
  if (view.localityLine.trim()) {
    lines.push(view.localityLine.trim());
  }
  return lines;
}

/**
 * Maps a Ship From or Ship To form value into review-card display fields.
 */
export function mapReviewAddress(address: AddressFormValue): ReviewAddressView {
  return {
    name: address.companyOrFullName.trim(),
    streetLines: streetLinesFrom(address),
    suiteAptUnit: address.suiteAptUnit.trim(),
    entryBuzzerCode: address.entryBuzzerCode.trim(),
    localityLine: localityLineFrom(address),
    contactName: address.attentionTo.trim(),
    email: address.email.trim(),
    phone: formatPhoneNumber(address.phoneNumber.trim(), address.phoneExtension),
  };
}

/**
 * Maps the return-address choice into either a "Same as Ship From" flag or a full address view.
 */
export function mapReviewReturnAddress(
  input: MapReviewReturnAddressInput,
): ReviewReturnAddressView {
  if (!input.useDifferentReturnAddress) {
    return { sameAsShipFrom: true, address: null };
  }

  return {
    sameAsShipFrom: false,
    address: {
      name: '',
      streetLines: deduplicateStreetLines(streetLinesFrom(input.returnAddress)),
      suiteAptUnit: input.returnAddress.suiteAptUnit.trim(),
      entryBuzzerCode: input.returnAddress.entryBuzzerCode.trim(),
      localityLine: localityLineFrom(input.returnAddress),
      contactName: '',
      email: '',
      // Session state persisted before OM-693 has no return phone fields.
      phone: formatPhoneNumber(
        (input.returnAddress.phoneNumber ?? '').trim(),
        input.returnAddress.phoneExtension,
      ),
    },
  };
}
